// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type Manufacturer,
  type UnitOfMeasure,
  useEvolvePaginatedGet,
  type WorkOrder,
  type WorkOrderItem,
} from '@evolvemep/api-ts';
import { Alert, Flex, Stack } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { useManufacturers } from 'hooks/typeCacheHooks/useManufacturers';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { AddItemsToWorkOrderMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/AddItemsToWorkOrderMenu';
import { getWorkOrderItemBomColDef } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/columnDefs';
import { DeleteWorkOrderItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/DeleteWorkOrderItem';
import { useBomItemEditing } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/useBomItemEditing';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

export type WorkOrderItemsTableAgContext = {
  manufacturers: Manufacturer[] | undefined;
  uoms: UnitOfMeasure[] | undefined;
};

type Props = {
  workOrder: WorkOrder;
  workOrderItem: WorkOrderItem;
} & (
  | {
      readOnly?: boolean;
      onItemsChanged: () => void;
    }
  | {
      readOnly: true;
      onItemsChanged?: () => void;
    }
);

export const WorkOrderItemBOM = ({
  workOrder,
  workOrderItem,
  onItemsChanged: onItemsChangedParent,
  readOnly,
}: Props) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const paginatedGetOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          workOrderId: workOrderItem.workOrderId,
          parentWorkOrderItemId: workOrderItem.workOrderItemId,
        },
      },
    }),
    [workOrderItem.workOrderId, workOrderItem.workOrderItemId],
  );
  const { fetchPage, abortController, setDefaultOpts } = useEvolvePaginatedGet('shop/workOrder/item', paginatedGetOpts);
  useEffect(() => {
    setDefaultOpts(paginatedGetOpts);
  }, [paginatedGetOpts, setDefaultOpts]);

  const editable = canEditWorkOrder('editItems') && !readOnly;

  const [deletingItem, setDeletingItem] = useState<WorkOrderItem | null>(null);

  const { agGridEditableProps, updateItem } = useBomItemEditing(onItemsChangedParent);

  const colDef = useMemo(
    () =>
      getWorkOrderItemBomColDef({
        parentItem: workOrderItem,
        canEditItems: editable,
        updateItem,
        onDeleteItem: setDeletingItem,
        compact: false,
      }),
    [editable, updateItem, workOrderItem],
  );

  const { uoms } = useUnitOfMeasure();
  const { manufacturers } = useManufacturers();
  const agGridContext = useMemo<WorkOrderItemsTableAgContext>(
    () => ({
      uoms,
      manufacturers,
    }),
    [manufacturers, uoms],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'item-bill-of-materials',
    tableViewType: TABLE_VIEW_TYPE.WORK_ORDER_ITEM_BILL_OF_MATERIALS,
    fetchPage,
    abortController,
    colDef,
    rowId: (r) => r.workOrderItemId,
    context: agGridContext,
  });

  const onItemsChanged = useCallback(() => {
    onItemsChangedParent?.();
    refreshGrid(gridApi);
  }, [onItemsChangedParent, gridApi]);

  useEffect(() => {
    refreshGrid(gridApi);
    // Refresh when a different item is selected
    // and when the item's quantity changes (which can impact totalQty of BOM items)
  }, [gridApi, /* effect dep */ workOrderItem.workOrderItemId, /* effect dep */ workOrderItem.quantity]);

  return (
    <>
      <Stack flex={1}>
        {workOrderItem.spoolHasManualEdits && (
          <Alert
            variant="light-mantine"
            color="orange"
            icon={<EvolveIcon icon="Alert" />}
            title="This BOM has been manually modified"
            pt="xs"
            pb="lg"
            styles={{ body: { gap: 0 } }}
          >
            A reupload from an external source (e.g. Revit) will overwrite any modifications.
          </Alert>
        )}

        {editable && (
          <Flex justify="flex-end">
            <AddItemsToWorkOrderMenu
              workOrder={workOrder}
              parentWorkOrderItem={workOrderItem}
              size="sm"
              onItemsChanged={onItemsChanged}
            />
          </Flex>
        )}
        <AgGridEditable gridApi={gridApi} {...agGridEditableProps}>
          {(applyWrap) => (
            <AgGridReact
              {...agGridProps}
              onFirstDataRendered={applyWrap}
              noRowsOverlayComponent={() => <NoRowsOverlay label="The bill of materials is empty for this item." />}
            />
          )}
        </AgGridEditable>
      </Stack>

      <DeleteWorkOrderItem
        workOrderItem={deletingItem}
        onClose={() => setDeletingItem(null)}
        onDeleted={onItemsChanged}
      />
    </>
  );
};
